import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useFlags } from 'launchdarkly-react-client-sdk';

import _ from 'lodash';
import { useObserver } from 'mobx-react';
import React, { useState } from 'react';
import { Link } from 'src/components/core';
import { LockIconSimple } from 'src/assets/lock-icon-simple';
import useBackInStock from 'src/hooks/use-back-in-stock';
import styled from 'styled-components';
import { space } from 'styled-system';

import useUser from 'hooks/use-user';
import { useExclusiveCollectionGate } from 'hooks/use-exclusive-collection-gate';
import { linkColors } from 'shared/constants/menu-colors';
import {
  POTENCY_TYPES,
  cbdDescription,
  tacDescription,
  terpsDescription,
  thcDescription,
} from 'shared/constants/products';
import {
  filterLegacyEffects,
  getCategoryForProduct,
  getFormattedPotency,
  getQuantityRemaining,
  getTaxInclusionNote,
  isWeightedProduct,
  strainEffects,
} from 'shared/helpers/products';
import { getDescriptionHtml, isDescriptionHtmlBlank } from 'shared/utils/product-description-utils';
import { getPotencyStrings, groupOptionsWithPrices } from 'src/utils/helpers/product';

import { ComingSoonChip, StaffPickChip } from 'components/chips';
import { HorizontalDivider } from 'components/core/divider';
import RichTextHtmlWrapper from 'shared/components/rich-text-html-wrapper';

import useCart from 'hooks/use-cart';
import { useViewedProductAnalytics } from 'hooks/use-ecommerce-analytics';
import useProductPrices from 'hooks/use-product-prices';
import { useProductPricing } from 'src/hooks/use-product-pricing';
import useProductQueryParams from 'hooks/use-product-query-params';
import useTranslation from 'hooks/use-translation';
import useUI from 'hooks/use-ui';
import usePaths from 'src/hooks/use-paths';
import { useListPriceTreatment } from 'src/hooks/use-list-price-treatment';
import { RemoteComponentSlot, useRemoteComponent } from 'hooks/use-remote-component';

import { useAddToCartModal } from 'components/modals/add-to-cart';
import ProductSpecialsCarousel from 'components/product-specials-carousel';
import { ProductSpecialsCarouselV2 } from 'src/components/product-specials-carousel-v2';
import { useRouter } from 'next/compat/router';
import { isExcludedCannabinoid } from 'shared/constants/cannabinoids';
import { getSpecialBadgeType, productIsPartOfActiveSpecial } from 'shared/helpers/specials';
import useProductOffers from 'src/hooks/use-product-offers';
import { useViewedProductMonitor } from 'utils/analytics/monitors/use-viewed-product-monitor';
import { useHardAgeGate } from 'src/hooks/use-hard-age-gate';
import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import { LoginCta } from 'src/components/ctas';
import { FlameIcon } from 'src/assets/flame-icon';
import { Typography } from 'src/typography';
import { ShareButton } from 'src/components/share-button';
import { useProductUrgencyEnabled } from 'src/hooks/use-product-urgency-enabled';
import { OutletCollectionTimerMessage } from 'src/components/outlet-collection-timer-message';

import { InfoChip } from '../info-chip';
import AdditionalOptionsSelect from './additional-options-select';
import BackInStock from './back-in-stock';
import { AddToCartButton, BuyingOptionsButton } from './buttons';
import {
  AddToCartContainer,
  AddToCartError,
  AddToCartErrorContainer,
  CarouselContainer,
  Content,
  Images,
  TaxText,
} from './layout';
import OptionsList from './options-list';
import { OptionsListV2 } from './options-list-v2';
import { Price, PriceFrom } from './price';
import ProductDetails from './product-details';
import QuantitySelect from './quantity-select';
import { AvailableFrom, Brand, Description, Name } from './typography';
import { PackagedWeight } from './packaged-weight';
import { ListPricePrice, OurPriceSection, OurPriceLabel, OurPriceValue } from './list-price-price';

const TagTooltipWidth = '348px';

export default function Product(props) {
  const { product, dispensary } = props;
  const { queryParams } = useProductQueryParams();
  const { isKiosk } = useUI();
  const { open: openAddToCartModal } = useAddToCartModal();
  const { t } = useTranslation();
  const { standardPrice, specialPrice, weightedSpecialPrices } = useProductPrices(product, queryParams.weight);
  const UI = useUI();
  const cart = useCart();
  const router = useRouter();
  const flags = useFlags();
  const isMedical = useObserver(() => cart.isMedical);
  const { href, route } = usePaths();
  const menuType = useObserver(() => cart.menuType);
  const linkColorData = _.find(linkColors, ({ key }) => key === dispensary?.colorSettings?.linkColor);
  const dispensaryCarouselDotColor = useObserver(() => linkColorData?.background ?? linkColors?.['blue-4']?.background);
  const { getPriceOptions } = useProductPricing();
  const { hardAgeGate: hideMenuContent } = useHardAgeGate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down(`sm`));
  const { specials, loading: specialsLoading } = useProductOffers(product);
  const { listPriceEnabled, hideSalesLanguage } = useListPriceTreatment();
  const hideDiscounts = useHideDiscountFromConsumer(dispensary) || hideSalesLanguage;
  const user = useUser();
  const { backInStockSubscription, handleUnsubscribeProduct, handleSubscribeProduct } = useBackInStock(product, user);
  const PrimaryDetails = useRemoteComponent(RemoteComponentSlot.ProductDetails);
  const isProductUrgencyEnabled = useProductUrgencyEnabled();
  useViewedProductAnalytics({ product, cart: _.cloneDeep(cart) });
  useViewedProductMonitor(product);
  useExclusiveCollectionGate({
    isExclusive: product.collectionCardBadge?.isExclusive,
    isAccessibleToCurrentUser: product.collectionCardBadge?.isAccessibleToCurrentUser,
    loading: !product,
  });

  // ===================
  // STATE
  // ===================
  const isProductCardsV2Enabled = flags['growth.ecomm.product-card-v2.rollout'] ?? false;
  const options = getPriceOptions(product);
  const parsedOptions = isProductCardsV2Enabled ? options : groupOptionsWithPrices({ product, menuType, isKiosk });
  const allOptionsOutOfStock = isProductCardsV2Enabled ? options.length === 0 : parsedOptions.length === 0;
  const [selectedOption, setSelectedOption] = useState(isProductCardsV2Enabled ? options?.[0] : parsedOptions?.[0]);
  const [selectedAdditionalOption, setAdditionalSelectedOption] = useState(product?.AdditionalOptions?.[0]);
  const [quantity, setQuantity] = useState(1);

  // ===================
  // DERIVED VALUES
  // ===================
  const maxQuantityDropdownOptions = 10;
  const taxInclusionNote =
    product.Status !== 'Archived' && getTaxInclusionNote({ product, dispensary, medical: isMedical });
  const isFeatured = product.featured?.current;
  const isComingSoon = product.comingSoon;
  const lowestPrice = !_.isNil(specialPrice) ? specialPrice : standardPrice;
  const showSpecialOffersCarousel =
    productIsPartOfActiveSpecial(product, menuType) && !hideDiscounts && !specialsLoading;
  const isGamificationFlagEnabled = flags['consumer.ecomm.gamification.rollout'] ?? false;
  const showTotalTerpenes = flags['ecomm.menu.show-total-terpenes'] ?? false;

  if (product.cannabinoidsV2) {
    product.cannabinoids = product.cannabinoidsV2;
  }

  const filteredEffects = filterLegacyEffects(product.effects);
  const hasEffects = !_.isEmpty(filteredEffects);
  const hasTerpenes = !_.isEmpty(product.terpenes) && !!product.terpenes.find((terp) => terp.value > 0);
  const hasCannabinoids =
    !_.isEmpty(product.cannabinoids) &&
    !_.isEmpty(_.filter(product.cannabinoids, ({ cannabinoid }) => !isExcludedCannabinoid(cannabinoid?.name)));
  const hideEffects = _.get(dispensary, `storeSettings.hideEffects`, false);
  const hideTerpenes = _.get(dispensary, `storeSettings.hideTerpenes`, false);
  const hasDescription = !isDescriptionHtmlBlank(getDescriptionHtml(product));
  const collapseProductDescription =
    hasDescription && _.get(dispensary, `storeSettings.collapseProductDescription`, false);
  const hasBrandDescription = !hideMenuContent && !_.isEmpty(_.trim(product.brand?.description));
  const hasCertificateOfAnalysis =
    product.certificateOfAnalysisEnabled && !_.isEmpty(product.POSMetaData?.canonicalLabResultUrl);
  const brandSlug = _.kebabCase(product.brand?.name) || product.brand?.id;
  const potency = getFormattedPotency(product);
  const canonicalPackageId = product.POSMetaData?.canonicalPackageId;
  const enabledStoreSettingPOSPackageID = dispensary.storeSettings.showPOSPackageId;
  const packageOptions =
    product.POSMetaData?.children?.map((child) => ({
      packageQuantity: child.packageQuantity,
      option: child.option,
    })) ?? [];
  const hasPrePackageOptions = packageOptions.some((option) => option.packageQuantity > 1);
  const badgeType = getSpecialBadgeType(product, product?.specialData?.saleSpecials, product?.Options);
  const showWeightSpecials = badgeType !== `none`;
  const lowInventoryMessageThreshold = 10;
  const isOutletCollectionsEnabled = flags['ecomm.display-outlet-collections-consumer.rollout'] ?? false;
  const showExclusiveCollectionBadge = product.collectionCardBadge?.isExclusive;
  const productName = product.name || product.Name;
  const shareButtonTop = (isMobile ? 0 : 14) + (product.brand.name && !isMobile ? 16 : 0);
  const isShareSupportedByBrowser = typeof navigator !== 'undefined' && 'share' in navigator;
  const shareButtonEnabled = isShareSupportedByBrowser && !isKiosk;

  // ===================
  // HELPER FUNCTIONS
  // ===================
  function getTotalQuantityAvailable(weight) {
    return getQuantityRemaining(product, weight, { isKiosk, defaultLimit: maxQuantityDropdownOptions });
  }

  function parseQuantityRemaining(weight) {
    return _.clamp(getTotalQuantityAvailable(weight), 1, maxQuantityDropdownOptions);
  }

  // ===================
  // COMPUTED VALUES (depend on helper functions)
  // ===================
  const quantityDropdownOptions = allOptionsOutOfStock ? 0 : parseQuantityRemaining(selectedOption.value);
  const totalQuantityAvailable = getTotalQuantityAvailable(selectedOption?.value);
  const isQuantityLimitMaxAvailableEnabled = dispensary?.storeSettings?.quantityLimitMaxAvailable ?? false;
  const enableMaxQuantityOption =
    totalQuantityAvailable > quantityDropdownOptions && isQuantityLimitMaxAvailableEnabled;
  const isQuantityOverMax = isQuantityLimitMaxAvailableEnabled && quantity > totalQuantityAvailable;
  const belowThreshold = isKiosk ? product.isBelowKioskThreshold : product.isBelowThreshold;
  const isUnavailable = product.Status !== 'Active' || belowThreshold || allOptionsOutOfStock;
  const displayLowInventoryMessage =
    !isUnavailable && totalQuantityAvailable <= lowInventoryMessageThreshold && isProductUrgencyEnabled;
  const showOutletCollectionTimer =
    isOutletCollectionsEnabled && product.outletCollectionInfo && !displayLowInventoryMessage;
  const hasProductMeta = showExclusiveCollectionBadge || displayLowInventoryMessage || showOutletCollectionTimer;

  // ===================
  // TAGS BUILDER
  // ===================
  const tags = [];

  if (product.strainType && _.isString(product.strainType) && product.strainType !== 'N/A') {
    tags.push({
      highlight: true,
      titleCase: true,
      id: `strainType`,
      queryValue: product.strainType.toLowerCase().replace(/\s+/g, '-'),
      text: product.strainType,
    });
  }

  if (potency.TAC && potency.TAC !== '--') {
    tags.push({
      id: `tac-content`,
      text: (
        <>
          <b>{POTENCY_TYPES.TAC}:</b> {potency.TAC}
        </>
      ),
      moreInfo: {
        infoTitle: t('marketplace.what-is-tac-header', 'What is TAC?'),
        infoBody: (
          <span>
            <b>{t('marketplace.tac-description-head', tacDescription.head)}&nbsp;</b>
            {t('marketplace.tac-description-body', tacDescription.body)}
          </span>
        ),
        tooltipDimensions: {
          width: TagTooltipWidth,
        },
      },
    });
  }

  if (potency.THC && potency.THC !== '--') {
    tags.push({
      id: `thc-content`,
      text: (
        <>
          <b>{POTENCY_TYPES.THC}:</b> {potency.THC}
        </>
      ),
      moreInfo: {
        infoTitle: t('marketplace.what-is-thc-header', 'What is THC?'),
        infoBody: (
          <span>
            <b>{t('marketplace.thc-description-head', thcDescription.head)}&nbsp;</b>
            {t('marketplace.thc-description-body', thcDescription.body)}
          </span>
        ),
        tooltipDimensions: {
          width: TagTooltipWidth,
        },
      },
    });
  }

  if (potency.CBD && potency.CBD !== '--') {
    tags.push({
      id: `cbd-content`,
      text: (
        <>
          <b>{POTENCY_TYPES.CBD}:</b> {potency.CBD}
        </>
      ),
      moreInfo: {
        infoTitle: t('marketplace.what-is-cbd-header', 'What is CBD?'),
        infoBody: (
          <span>
            <b>{t('marketplace.cbd-description-head', cbdDescription.head)}&nbsp;</b>
            {t('marketplace.cbd-description-body', cbdDescription.body)}
          </span>
        ),
        tooltipDimensions: {
          width: TagTooltipWidth,
        },
      },
    });
  }

  if (showTotalTerpenes) {
    const terpsString = getPotencyStrings(product).TERPS;

    if (terpsString) {
      tags.push({
        id: `terps-content`,
        text: (
          <>
            <b>TERPENES:</b> {terpsString}
          </>
        ),
        moreInfo: {
          infoTitle: t('marketplace.what-is-terps-header', 'What are Terpenes?'),
          infoBody: (
            <span>
              <b>{t('marketplace.terps-description-head', terpsDescription.head)}&nbsp;</b>
              {t('marketplace.terps-description-body', terpsDescription.body)}
            </span>
          ),
          tooltipDimensions: {
            width: TagTooltipWidth,
          },
        },
      });
    }
  }

  const hasTags = tags.length > 0;

  // ===================
  // EVENT HANDLERS
  // ===================
  function handleToggleBuyingOptionsModal() {
    UI.productBuyingOptionsModal = product;
  }

  function handleOptionSelect(option) {
    const newQuantityRemaining = parseQuantityRemaining(option?.value);

    if (quantity > newQuantityRemaining && !isQuantityLimitMaxAvailableEnabled) {
      setQuantity(1);
    }

    setSelectedOption(option);
  }

  function handleQuantityChange({ target }) {
    setQuantity(Number(target.value));
  }

  const handleAddToCart = async () => {
    openAddToCartModal({
      quantity,
      additionalOption: selectedAdditionalOption,
      option: selectedOption.value,
      product,
      dispensary,
      trackerSource: UI.activeProductSource,
    });
  };

  async function handleCarouselClick(offerId) {
    await router?.push(`/${UI.dispensaryRootUrl}/${dispensary?.cName}/specials/offer/${offerId}`);
  }

  async function navigateToStrainTypePage(tag) {
    // eslint-disable-next-line lodash/prefer-lodash-method
    const isInfoTextLink = tag.id === `strainType` && Object.keys(strainEffects).some((st) => st === tag.text);
    const dispensaryCName = dispensary?.cName;
    const category = getCategoryForProduct(product).key;

    if (isInfoTextLink && dispensaryCName && category) {
      await router?.push(
        `/${UI.dispensaryRootUrl}/${dispensaryCName}/products/${category}?straintypes=${tag.queryValue}`
      );
    }
  }

  return (
    <ProductContent>
      {shareButtonEnabled && (
        <ShareButtonContainer top={shareButtonTop}>
          <ShareButton title={productName} />
        </ShareButtonContainer>
      )}
      <Images product={product} isUnavailable={isUnavailable} />

      <Details data-testid='product-details-primary'>
        {isUnavailable && <OutOfStockText>Out of stock</OutOfStockText>}
        {product.brand && product.brand?.id && (
          <Brand>
            <Link route={`${route}/brands/${brandSlug}`} href={`${href}/brands/${brandSlug}`}>
              {product.brand.name}
            </Link>
          </Brand>
        )}
        <NameWithShareButton
          data-testid='product-name'
          tag='h1'
          size='xlarge'
          rightPadding={shareButtonEnabled && !isMobile ? 40 : 0}
        >
          {productName}
        </NameWithShareButton>
        {(isFeatured || isComingSoon) && (
          <ChipContainer mb='16px'>
            {isFeatured && <StaffPickChip />}
            {isComingSoon && <ComingSoonChip />}
          </ChipContainer>
        )}

        {hasProductMeta && (
          <ProductMeta>
            {showExclusiveCollectionBadge && (
              <ExclusiveLabel $color={product.collectionCardBadge.color}>
                <LockIconSimple color='currentColor' />
                <span>{product.collectionCardBadge.title}</span>
              </ExclusiveLabel>
            )}

            {displayLowInventoryMessage && (
              <LowInventoryMessageContainer data-testid='product-low-inventory-message-container'>
                <FlameIcon />
                <LowInventoryMessage data-testid='product-low-inventory-message' size='medium'>
                  {totalQuantityAvailable} left in stock – order soon!
                </LowInventoryMessage>
              </LowInventoryMessageContainer>
            )}
            {showOutletCollectionTimer && (
              <OutletCollectionTimerMessage
                endDate={product.outletCollectionInfo.endDate}
                message={product.outletCollectionInfo.timerMessage}
              />
            )}
          </ProductMeta>
        )}
        {dispensary && (
          <>
            {isWeightedProduct(product) ? (
              <WeightedProductOptions
                product={product}
                isProductCardsV2Enabled={isProductCardsV2Enabled}
                listPriceEnabled={listPriceEnabled}
                parsedOptions={parsedOptions}
                selectedOption={selectedOption}
                hideDiscounts={hideDiscounts}
                hideSalesLanguage={hideSalesLanguage}
                isGamificationFlagEnabled={isGamificationFlagEnabled}
                hasPrePackageOptions={hasPrePackageOptions}
                packageOptions={packageOptions}
                handleOptionSelect={handleOptionSelect}
                showWeightSpecials={showWeightSpecials}
                weightedSpecialPrices={weightedSpecialPrices}
                isComingSoon={isComingSoon}
                isUnavailable={isUnavailable}
              />
            ) : (
              <Price
                price={lowestPrice}
                isSpecial={!_.isNil(specialPrice)}
                standardPrice={standardPrice}
                isMobile={isMobile}
                isUnavailable={isUnavailable}
                hideDiscount={hideDiscounts}
                listPriceEnabled={listPriceEnabled}
                hideSalesLanguage={hideSalesLanguage}
              />
            )}

            {!UI.viewOnlyMode && !isComingSoon && !isUnavailable && (
              <AddToCartContainer>
                {!_.isEmpty(product?.AdditionalOptions) && (
                  <AdditionalOptionsSelect
                    options={product.AdditionalOptions}
                    value={selectedAdditionalOption}
                    onChange={setAdditionalSelectedOption}
                  />
                )}
                <QuantitySelect
                  onChange={handleQuantityChange}
                  value={quantity}
                  quantityDropdownOptions={quantityDropdownOptions}
                  enableMaxQuantityOption={enableMaxQuantityOption}
                  data-testid='quantity-select'
                />
                <ButtonWrapper data-testid='add-to-cart' isNewLine={!_.isEmpty(product?.AdditionalOptions)}>
                  <AddToCartButton
                    isMobile={isMobile}
                    onClick={handleAddToCart}
                    id='add-to-cart'
                    disabled={!quantity || isQuantityOverMax}
                  />
                </ButtonWrapper>
              </AddToCartContainer>
            )}
            {isQuantityOverMax && (
              <AddToCartErrorContainer includeBottomMargin={!(taxInclusionNote && !isUnavailable)}>
                <AddToCartError size='small' tag='div' data-testid='error-quantity-over-max'>
                  {totalQuantityAvailable} max quantity available
                </AddToCartError>
              </AddToCartErrorContainer>
            )}
            {taxInclusionNote && !isUnavailable && <TaxText>{taxInclusionNote}</TaxText>}
            {!isKiosk && (
              <BackInStock
                isUnavailable={isUnavailable}
                backInStockSubscription={backInStockSubscription}
                user={user}
                handleSubscribeProduct={handleSubscribeProduct}
                handleUnsubscribeProduct={handleUnsubscribeProduct}
                dispensary={dispensary}
              />
            )}
          </>
        )}
        {!dispensary && (
          <>
            <PriceFrom price={lowestPrice} />
            <HorizontalDivider mb={31} />
            <AvailableFrom numStores={7} />
            <BuyingOptionsButton onClick={handleToggleBuyingOptionsModal} />
          </>
        )}
        {showSpecialOffersCarousel && (
          <>
            <HorizontalDivider mb={isMobile ? 31 : 38} />
            <CarouselContainer>
              {isGamificationFlagEnabled ? (
                <ProductSpecialsCarouselV2 specials={specials} />
              ) : (
                <ProductSpecialsCarousel
                  specials={specials}
                  dotColor={dispensaryCarouselDotColor}
                  onClick={handleCarouselClick}
                />
              )}
            </CarouselContainer>
          </>
        )}
        {/* Tags & Description */}
        {(hasTags || hasDescription) && (
          <>
            <HorizontalDivider mb={isMobile ? 31 : 38} />
            {hasTags && (
              <Tags>
                {_.map(tags, (tag) => (
                  <InfoChip key={tag.id} {...tag} onClick={() => navigateToStrainTypePage(tag)} />
                ))}
              </Tags>
            )}
            {hasDescription && !collapseProductDescription && (
              <Description>
                <RichTextHtmlWrapper html={getDescriptionHtml(product)} />
              </Description>
            )}
          </>
        )}
        {hasCertificateOfAnalysis && (
          <CertificateOfAnalysisLink href={product.POSMetaData.canonicalLabResultUrl} target='_blank' rel='noreferrer'>
            Certificate of Analysis
          </CertificateOfAnalysisLink>
        )}
        {enabledStoreSettingPOSPackageID && canonicalPackageId && (
          <POSPackageIdSpan>Package ID: {canonicalPackageId}</POSPackageIdSpan>
        )}
        <ProductDetails
          product={product}
          collapseProductDescription={collapseProductDescription}
          hasEffects={hasEffects}
          hideEffects={hideEffects}
          hasTerpenes={hasTerpenes}
          hideTerpenes={hideTerpenes}
          hideDiscounts={hideDiscounts}
          hasCannabinoids={hasCannabinoids}
          hasBrandDescription={hasBrandDescription}
          isMobile={isMobile}
        />

        <PrimaryDetails />

        <LoginCta />
      </Details>
    </ProductContent>
  );
}

const ProductMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-block: 16px 24px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
    gap: 16px;
    align-items: center;
  }
`;

const ExclusiveLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  svg {
    width: 20px;
    height: 20px;
  }
`;

/**
 * @param {object} props
 * @param {object} props.selectedOption - Price option with label, value, formattedPrice, special, formattedDiscount
 * @param {boolean} props.hideDiscounts - Whether discounts are hidden (admin setting or compliance)
 * @param {boolean} props.hideSalesLanguage - Whether compliance-friendly language is enabled
 * @param {boolean} props.showPackagedWeight - Whether to render PackagedWeight component
 * @param {Array} props.packageOptions - Package options for PackagedWeight
 */
function SingleWeightListPrice({
  selectedOption,
  hideDiscounts,
  hideSalesLanguage,
  showPackagedWeight,
  packageOptions,
}) {
  const discount = selectedOption.formattedDiscount?.replace(/\s*off$/i, '');
  const savingsLabel = discount ? `You save ${discount}` : undefined;

  return (
    <SingleWeightSection>
      <WeightLabel>Weight: {selectedOption.label}</WeightLabel>
      {/* Allow compliance mode (hideSalesLanguage) to still show list price without sales language */}
      {selectedOption?.special && !(hideDiscounts && !hideSalesLanguage) ? (
        <ListPricePrice
          specialPrice={selectedOption.special.formattedPrice}
          standardPrice={selectedOption.formattedPrice}
          savingsLabel={savingsLabel}
          hideSalesLanguage={hideSalesLanguage}
        />
      ) : (
        <OurPriceSection>
          <OurPriceLabel>Our price:</OurPriceLabel>
          <OurPriceValue>{selectedOption.formattedPrice}</OurPriceValue>
        </OurPriceSection>
      )}
      {showPackagedWeight && <PackagedWeight packageOptions={packageOptions} selectedOption={selectedOption.value} />}
    </SingleWeightSection>
  );
}

function WeightedProductOptions({
  product,
  isProductCardsV2Enabled,
  listPriceEnabled,
  parsedOptions,
  selectedOption,
  hideDiscounts,
  hideSalesLanguage,
  isGamificationFlagEnabled,
  hasPrePackageOptions,
  packageOptions,
  handleOptionSelect,
  showWeightSpecials,
  weightedSpecialPrices,
  isComingSoon,
  isUnavailable,
}) {
  if (!isProductCardsV2Enabled) {
    return (
      <OptionsList
        onOptionSelect={handleOptionSelect}
        options={parsedOptions}
        product={product}
        data-testid='options-list'
        selectedOption={selectedOption}
        showWeightSpecials={showWeightSpecials}
        weightedSpecialPrices={weightedSpecialPrices}
        disabled={isComingSoon || isUnavailable}
        hideDiscount={hideDiscounts}
      />
    );
  }

  if (parsedOptions.length === 1 && listPriceEnabled) {
    return (
      <SingleWeightListPrice
        selectedOption={selectedOption}
        hideDiscounts={hideDiscounts}
        hideSalesLanguage={hideSalesLanguage}
        showPackagedWeight={isGamificationFlagEnabled && hasPrePackageOptions}
        packageOptions={packageOptions}
      />
    );
  }

  // Gate compliance bypass: only when flag is ON does hideSalesLanguage override hideDiscounts
  const shouldHideDiscount = listPriceEnabled ? hideDiscounts && !hideSalesLanguage : hideDiscounts;

  return (
    <OptionsWrapper>
      <OptionsListV2
        options={parsedOptions}
        hideDiscount={shouldHideDiscount}
        selectedOption={selectedOption}
        onClick={handleOptionSelect}
      />
      {isGamificationFlagEnabled && hasPrePackageOptions && (
        <PackagedWeight packageOptions={packageOptions} selectedOption={selectedOption.value} />
      )}
    </OptionsWrapper>
  );
}

const OptionsWrapper = styled.div`
  margin: 16px 0 32px;
`;

const SingleWeightSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
`;

const WeightLabel = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 15px;
  letter-spacing: 0.005em;
  color: ${({ theme }) => theme.colors.primaryBlack};
`;

const ProductContent = styled(Content)`
  position: relative;
`;

const Details = styled.div`
  flex: 1 0 0%;
  min-width: 0;
  margin: 0;
  margin-top: 15px;
  width: 100%;
  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin: 0 28px;
  }
`;

const CertificateOfAnalysisLink = styled.a`
  color: #3287e1;
  cursor: pointer;
  font-weight: bold;
  font-size: 13px;
  margin-bottom: 2rem;
`;

const ChipContainer = styled.div`
  ${space}

  .MuiChip-root {
    font-size: 14px;
    padding: 3px;
    margin-right: 8px;
  }
`;

const Tags = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  font-family: var(--dct--customized-fonts-secondary);
  margin-bottom: 32px;
  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin-bottom: 23px;
  }
`;

const ButtonWrapper = styled.div`
  @media only screen and (max-width: 450px) {
    margin-top: ${({ isNewLine }) => (isNewLine ? `15px` : `0px`)};
  }
`;

const OutOfStockText = styled.div`
  color: ${({ theme }) => theme.colors.grey[60]};
  font-size: 0.8125rem;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  text-transform: uppercase;
  margin-bottom: 1rem;
`;

const LowInventoryMessage = styled(Typography.Title)`
  font-weight: 500;
`;

const LowInventoryMessageContainer = styled.div`
  color: var(--dct--customized-colors-discountTag);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NameWithShareButton = styled(Name)`
  padding-right: ${({ rightPadding = 0 }) => `${rightPadding}px`};
`;

const ShareButtonContainer = styled.div`
  position: absolute;
  right: 0;
  top: ${({ top = 0 }) => `${top}px`};
  padding: 12px 0 12px 12px;
`;

const POSPackageIdSpan = styled.span`
  color: #646d72;
  font-size: 11px;
  font-weight: 400;
  line-height: 14px;
`;
